import { css } from '@emotion/react'
import { colors } from 'bold-ui'
import { TableColumnConfig } from 'bold-ui/lib/components/Table/DataTable/DataTable'
import { IconLabel } from 'components/IconLabel'
import {
  AtividadeColetiva,
  FatoVisitaDomiciliar,
  TipoAcompanhamentoEnum,
  TipoRegistroHistoricoClinico,
} from 'graphql/types.generated'
import { calculateIdadeGestacional } from 'util/atendimento/gestante/calculateIdadeGestacional'
import { isUndefinedOrNull } from 'util/checks'
import { formatDate, toDate } from 'util/date/formatDate'
import { IS_TIPO_ATIVIDADE_REUNIAO, tipoAtividadeRecord } from 'view/atividade-coletiva/model-atividadeColetiva'
import { formatNomeProfissional } from 'view/profissional/utils/utils-profissional'

import { AcompanhamentoUltimosContatosModel } from '../folha-rosto/components/ultimos-contatos/converter-ultimosContatos'
import { DetalhesUltimosContatosButton } from '../folha-rosto/components/ultimos-contatos/detalhes/DetalhesUltimosContatosButton'
import { ProfissionalListLabel } from '../historico/components/accordion/detail/ProfissionalListLabel'
import { tipoRegistroHistoricoData } from '../historico/model/model-historico'
import { formatIdadeGestacional } from '../soap/pre-natal/util-preNatal'
import {
  convertAcompanhamentoUltimosContatosToContatosModel,
  convertAtividadeColetivaToContatosModel,
  convertFatoVisitaDomiciliarToContatosModel,
  getTipoRegistroAcompanhamentoUltimosContatos,
} from './converter-acompanhamentos'
import { statusVisitaDomiciliarRecord, UltimosContatosTabs } from './model-acompanhamentos'
import { getTipoPuerperio } from './puerperio/util-acompanhamentoPuerperio'

const renderStatusConsultaPuerperio = (consulta: AcompanhamentoUltimosContatosModel) => {
  if (!isUndefinedOrNull(consulta?.codigoUnicoRegistro)) {
    return <IconLabel icon='checkCircleOutline' text='Realizado' color={colors.green.c40} hSpacing={0.5} />
  }
  return <IconLabel icon='timesOutline' text='Não realizado' color={colors.red.c40} hSpacing={0.5} />
}

export const renderStatusVisita = (visita: FatoVisitaDomiciliar) => {
  const status = statusVisitaDomiciliarRecord[visita.dimensaoDesfechoVisita?.descricao]
  return <IconLabel icon={status.icon} text={status.label} color={status.color} hSpacing={0.5} />
}

export const renderProfissionalConsulta = (consulta: AcompanhamentoUltimosContatosModel) => (
  <ProfissionalListLabel
    nome={consulta.profissional?.nome}
    temNomeSocial={!!consulta.profissional?.nomeSocial}
    cbo={consulta.cbo?.nome}
    ineEquipe={consulta.equipe?.ine}
    nomeEquipe={consulta.equipe?.nome}
  />
)

export const renderProfissionalVisita = (visita: FatoVisitaDomiciliar) => {
  const profissional = visita.dimensaoProfissional?.profissional

  return (
    <ProfissionalListLabel
      nome={profissional?.nome ?? visita.dimensaoProfissional?.nome}
      temNomeSocial={!!profissional?.nomeSocial}
      cbo={visita.dimensaoCbo?.nome}
      nomeEquipe={visita.dimensaoEquipe?.nome}
      ineEquipe={visita.dimensaoEquipe?.ine}
    />
  )
}

const renderProfissionalCuidadoCompartilhado = (consulta: AcompanhamentoUltimosContatosModel) => {
  const lotacaoSolicitante = consulta.cuidadoCompartilhado?.lotacaoSolicitante

  const profissional = lotacaoSolicitante?.profissional
  const equipe = lotacaoSolicitante?.equipe

  return (
    <ProfissionalListLabel
      nome={profissional?.nome ?? consulta.nomeSolicitante}
      temNomeSocial={!!profissional?.nomeSocial}
      cbo={lotacaoSolicitante?.cbo?.nome ?? consulta.cboSolicitante}
      ineEquipe={equipe?.ine ?? consulta.ineSolicitante}
      nomeEquipe={equipe?.nome ?? consulta.siglaEquipeSolicitante}
    />
  )
}

const renderTipoPuerperio = (consulta: AcompanhamentoUltimosContatosModel, dataInicioPuerperio: Date) => {
  return getTipoPuerperio(consulta?.dataAtendimento, dataInicioPuerperio)
}

const renderTipoRegistroOutrasConsultas = (consulta: AcompanhamentoUltimosContatosModel) => {
  const tipoAtendimentoEnum = getTipoRegistroAcompanhamentoUltimosContatos(consulta)
  const historicoDataRecord = tipoRegistroHistoricoData[tipoAtendimentoEnum.toString()]
  return historicoDataRecord.descricao
}

const renderVisualizarHistorico = (consulta: AcompanhamentoUltimosContatosModel, dataNascimentoCidadao: LocalDate) =>
  (consulta?.codigoUnicoRegistro || isCuidadoCompartilhado(consulta)) && (
    <DetalhesUltimosContatosButton
      contato={convertAcompanhamentoUltimosContatosToContatosModel(consulta)}
      dataNascimentoCidadao={dataNascimentoCidadao}
    />
  )

const renderVisualizarVisita = (visita: FatoVisitaDomiciliar, dataNascimentoCidadao: LocalDate) => (
  <DetalhesUltimosContatosButton
    contato={convertFatoVisitaDomiciliarToContatosModel(visita)}
    dataNascimentoCidadao={dataNascimentoCidadao}
  />
)

const renderVisualizarAtividadeColetiva = (atividadeColetiva: AtividadeColetiva, dataNascimentoCidadao: LocalDate) => (
  <DetalhesUltimosContatosButton
    contato={convertAtividadeColetivaToContatosModel(atividadeColetiva)}
    dataNascimentoCidadao={dataNascimentoCidadao}
  />
)

const renderIg = (dataRegistro: Date | LocalDate, dataInicioGestacao: Date) => {
  if (!dataRegistro) return '-'
  const dataRef = dataRegistro instanceof Date ? dataRegistro : toDate(dataRegistro)

  const igConsulta = calculateIdadeGestacional(dataInicioGestacao, dataRef)
  if (!igConsulta.hasIdadeGestacional) return '-'

  return formatIdadeGestacional(igConsulta.emDias) ?? '-'
}

const renderDataAtendimento = (consulta: AcompanhamentoUltimosContatosModel) => {
  return consulta?.codigoUnicoRegistro || isCuidadoCompartilhado(consulta) ? formatDate(consulta.dataAtendimento) : '-'
}

const renderProfissional = (consulta: AcompanhamentoUltimosContatosModel) => {
  if (isCuidadoCompartilhado(consulta)) {
    return renderProfissionalCuidadoCompartilhado(consulta)
  }

  return consulta?.profissional ? renderProfissionalConsulta(consulta) : '-'
}

const isCuidadoCompartilhado = (consulta: AcompanhamentoUltimosContatosModel) =>
  consulta?.tipoApresentacao === TipoRegistroHistoricoClinico.CUIDADO_COMPARTILHADO

const getVisualizarColumn = <T,>(renderFn: (item: T) => React.ReactNode): TableColumnConfig => ({
  name: 'visualizar',
  header: '',
  style: styles.lupa,
  render: renderFn,
})

const getBaseConsultasColumns = (): TableColumnConfig[] => [
  {
    name: 'dataAtendimento',
    header: 'Data',
    render: renderDataAtendimento,
  },
  {
    name: 'profissional',
    header: 'Profissional',
    render: renderProfissional,
  },
]

const getBaseVisitasColumns = (): TableColumnConfig[] => [
  {
    name: 'dataVisita',
    header: 'Data',
    render: (visita: FatoVisitaDomiciliar) => formatDate(visita.dataVisita),
  },
  {
    name: 'status',
    header: 'Status',
    render: (visita: FatoVisitaDomiciliar) => renderStatusVisita(visita),
  },
  { name: 'profissional', header: 'Profissional', render: renderProfissionalVisita },
]

const getBaseAtividadesColetivasColumns = (dataNascimentoCidadao: LocalDate): TableColumnConfig[] => [
  {
    name: 'dataAtividade',
    header: 'Data',
    render: (atividadeColetiva: AtividadeColetiva) => formatDate(atividadeColetiva.dataAtividade),
  },
  {
    name: 'tipoRegistro',
    header: 'Tipo de registro',
    render: (atividadeColetiva: AtividadeColetiva) =>
      IS_TIPO_ATIVIDADE_REUNIAO[atividadeColetiva.tipoAtividade]
        ? 'Atividade em reunião'
        : 'Atividade coletiva em saúde',
  },
  {
    name: 'tipoAtividade',
    header: 'Tipo de atividade',
    render: (atividadeColetiva: AtividadeColetiva) => tipoAtividadeRecord[atividadeColetiva.tipoAtividade],
  },
  {
    name: 'profissionalResponsavel',
    header: 'Responsável',
    render: (atividadeColetiva: AtividadeColetiva) =>
      formatNomeProfissional(atividadeColetiva.lotacaoResponsavel.profissional),
  },
  {
    name: 'visualizarHistorico',
    header: '',
    style: styles.lupa,
    render: (atividadeColetiva: AtividadeColetiva) =>
      renderVisualizarAtividadeColetiva(atividadeColetiva, dataNascimentoCidadao),
  },
]

const getBaseOutrasConsultasColumns = (dataNascimentoCidadao: LocalDate): TableColumnConfig[] => [
  ...getBaseConsultasColumns(),
  {
    name: 'tipo',
    header: 'Tipo de registro',
    render: renderTipoRegistroOutrasConsultas,
  },
  getVisualizarColumn<AcompanhamentoUltimosContatosModel>((consulta) =>
    renderVisualizarHistorico(consulta, dataNascimentoCidadao)
  ),
]

export const getUltimosContatosColumns = (
  tipoAcompanhamento: TipoAcompanhamentoEnum,
  dataInicioCondicao: Date,
  dataNascimentoCidadao: LocalDate
): Record<UltimosContatosTabs, TableColumnConfig[]> | undefined => {
  const columns: Partial<Record<TipoAcompanhamentoEnum, Record<UltimosContatosTabs, TableColumnConfig[]>>> = {
    [TipoAcompanhamentoEnum.PUERPERIO]: {
      [UltimosContatosTabs.CONSULTAS]: [
        ...getBaseConsultasColumns(),

        { name: 'status', header: 'Status', render: renderStatusConsultaPuerperio },
        {
          name: 'tipo',
          header: 'Tipo',
          render: (consulta: AcompanhamentoUltimosContatosModel) => renderTipoPuerperio(consulta, dataInicioCondicao),
        },
        getVisualizarColumn<AcompanhamentoUltimosContatosModel>((consulta) =>
          renderVisualizarHistorico(consulta, dataNascimentoCidadao)
        ),
      ],

      [UltimosContatosTabs.VISITAS_DOMICILIARES]: [
        ...getBaseVisitasColumns(),
        getVisualizarColumn<FatoVisitaDomiciliar>((visita) => renderVisualizarVisita(visita, dataNascimentoCidadao)),
      ],
      [UltimosContatosTabs.ATIVIDADE_COLETIVA]: [...getBaseAtividadesColetivasColumns(dataNascimentoCidadao)],
      [UltimosContatosTabs.OUTRAS_CONSULTAS]: [...getBaseOutrasConsultasColumns(dataNascimentoCidadao)],
    },

    [TipoAcompanhamentoEnum.PRE_NATAL]: {
      [UltimosContatosTabs.CONSULTAS]: [
        ...getBaseConsultasColumns(),
        {
          name: 'igConsulta',
          header: 'IG na consulta',
          render: (consulta: AcompanhamentoUltimosContatosModel) =>
            renderIg(consulta?.dataAtendimento, dataInicioCondicao),
        },
        getVisualizarColumn<AcompanhamentoUltimosContatosModel>((consulta) =>
          renderVisualizarHistorico(consulta, dataNascimentoCidadao)
        ),
      ],

      [UltimosContatosTabs.VISITAS_DOMICILIARES]: [
        ...getBaseVisitasColumns(),
        {
          name: 'igConsulta',
          header: 'IG na visita',
          render: (visita: FatoVisitaDomiciliar) => renderIg(visita?.dataVisita, dataInicioCondicao),
        },
        getVisualizarColumn<FatoVisitaDomiciliar>((visita) => renderVisualizarVisita(visita, dataNascimentoCidadao)),
      ],
      [UltimosContatosTabs.ATIVIDADE_COLETIVA]: [...getBaseAtividadesColetivasColumns(dataNascimentoCidadao)],
      [UltimosContatosTabs.OUTRAS_CONSULTAS]: [],
    },
  }

  return columns[tipoAcompanhamento]
}

const styles = {
  lupa: css`
    padding: 0;
  `,
}
